import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import EmailRegistrationCard from './EmailRegistrationCard';
import StravaClubCard from './StravaClubCard';
import ReferralCard from './ReferralCard';
import ChallengeRewardCard from './ChallengeRewardCard';
import './EarnDiamondsChecklist.css';

const TOTAL_STARTER_REWARDS = 2;
const REWARD_VALUE = 5;

// The earn list shared by the nav diamonds panel and the /diamonds page: the
// Email + Strava Club starter rewards, the repeatable referral, and a Challenges
// card that appears once both starters are done.
//
// `hideClaimed` (panel) drops each starter card the moment it's claimed, so the
// panel only ever shows what's left to do. The /diamonds hub leaves them visible
// in their claimed state. Claim state is seeded once and advanced by in-session
// claim callbacks; the child cards keep their own claim/verify logic.
//
// `rewardStatus` (optional): a getCreditRewardStatus result fetched by the parent.
// The nav prefetches it at page load and passes it in so opening the diamonds
// panel is instant (`null` = parent still fetching). When the prop is absent
// (the /diamonds page), the checklist fetches its own copy.
const EarnDiamondsChecklist = ({
  user,
  onRewardClaimed = null,
  variant = 'section',
  showHeader = true,
  compact = false,
  hideClaimed = false,
  rewardStatus = undefined,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [baseEmail, setBaseEmail] = useState(false);
  const [baseStrava, setBaseStrava] = useState(false);
  const [sessionClaims, setSessionClaims] = useState(() => new Set());

  // Parent-supplied status: seed from the prop, no fetch of our own.
  useEffect(() => {
    if (rewardStatus === undefined) return;
    if (!user) {
      setLoading(false);
      return;
    }
    if (rewardStatus === null) return; // parent fetch still in flight
    setBaseEmail(!!rewardStatus.email?.claimed);
    setBaseStrava(!!rewardStatus.stravaClub?.claimed);
    setLoading(false);
  }, [rewardStatus, user]);

  // Standalone mode (no prop): fetch the status once.
  useEffect(() => {
    if (rewardStatus !== undefined) return undefined;
    let cancelled = false;
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const getStatus = httpsCallable(functions, 'getCreditRewardStatus');
        const result = await getStatus();
        if (!cancelled) {
          setBaseEmail(!!result.data?.email?.claimed);
          setBaseStrava(!!result.data?.stravaClub?.claimed);
        }
      } catch (err) {
        console.error('[EarnDiamondsChecklist] failed to load reward status:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleClaimed = (key) => {
    setSessionClaims((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (onRewardClaimed) onRewardClaimed();
  };

  const emailClaimed = baseEmail || sessionClaims.has('email');
  const stravaClaimed = baseStrava || sessionClaims.has('strava');
  const bothClaimed = emailClaimed && stravaClaimed;
  const totalClaimed = (emailClaimed ? 1 : 0) + (stravaClaimed ? 1 : 0);
  const remainingDiamonds = (TOTAL_STARTER_REWARDS - totalClaimed) * REWARD_VALUE;

  if (!user) return null;
  if (loading) {
    return (
      <div className="earn-diamonds-cluster__loading" aria-label="Loading rewards">
        <span className="earn-diamonds-cluster__spinner" />
      </div>
    );
  }

  // In hideClaimed mode (panel) a claimed starter card is removed entirely; the
  // hub keeps it visible in its claimed state via alwaysVisible.
  const showEmail = !hideClaimed || !emailClaimed;
  const showStrava = !hideClaimed || !stravaClaimed;

  return (
    <div className={`earn-diamonds-cluster earn-diamonds-cluster--${variant}${compact ? ' earn-diamonds-cluster--compact' : ''}`}>
      {showHeader && (
        <div className="earn-diamonds-cluster__header">
          <h3 className="earn-diamonds-cluster__title">
            🎁 {t('earnDiamonds.title', 'Earn your first diamonds')}
          </h3>
          <p className="earn-diamonds-cluster__subtitle">
            {totalClaimed >= TOTAL_STARTER_REWARDS
              ? t('earnDiamonds.allDone', 'Starter rewards claimed. Keep earning below.')
              : t('earnDiamonds.meter', '{{claimed}} of {{total}} claimed · earn up to {{remaining}} 💎', {
                  claimed: totalClaimed,
                  total: TOTAL_STARTER_REWARDS,
                  remaining: remainingDiamonds,
                })}
          </p>
          <div
            className="earn-diamonds-cluster__meter"
            role="progressbar"
            aria-valuenow={totalClaimed}
            aria-valuemin={0}
            aria-valuemax={TOTAL_STARTER_REWARDS}
          >
            <div
              className="earn-diamonds-cluster__meter-fill"
              style={{ width: `${(totalClaimed / TOTAL_STARTER_REWARDS) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="earn-diamonds-cluster__cards">
        {showEmail && (
          <EmailRegistrationCard
            user={user}
            alwaysVisible={!hideClaimed}
            onRewardClaimed={() => handleClaimed('email')}
          />
        )}
        {showStrava && (
          <StravaClubCard
            user={user}
            onRewardClaimed={() => handleClaimed('strava')}
          />
        )}
        <ReferralCard onRewardClaimed={onRewardClaimed} />
        {bothClaimed && <ChallengeRewardCard />}
      </div>
    </div>
  );
};

export default EarnDiamondsChecklist;
